import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Plus, X, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
    active: "border-emerald-400/40 text-emerald-400",
    paused: "border-amber-400/40 text-amber-400",
    archived: "border-white/15 text-bone-500",
    pending: "border-ember/60 text-ember",
};

export default function AdminClients() {
    const { user, token, loading } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [busy, setBusy] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        plan: "",
        plan_summary: "",
        manager_name: "",
        manager_role: "",
        manager_email: "",
    });

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () => {
        setBusy(true);
        return axios
            .get(`${API}/admin/clients`, { headers })
            .then((r) => setClients(r.data))
            .catch(() => toast.error("Could not load clients"))
            .finally(() => setBusy(false));
    };

    useEffect(() => {
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const onCreate = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Name and email required");
            return;
        }
        try {
            const payload = {};
            Object.entries(form).forEach(([k, v]) => {
                if (v) payload[k] = v;
            });
            const { data } = await axios.post(
                `${API}/admin/clients`,
                payload,
                { headers },
            );
            toast.success(`Invite sent to ${data.email}`);
            setShowNew(false);
            setForm({
                name: "",
                email: "",
                company: "",
                plan: "",
                plan_summary: "",
                manager_name: "",
                manager_role: "",
                manager_email: "",
            });
            refresh();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not create client";
            toast.error(typeof msg === "string" ? msg : "Failed");
        }
    };

    if (loading) return null;
    if (!user) return <Navigate to="/admin/login" replace />;

    return (
        <AdminShell>
            <div className="flex items-end justify-between gap-4 mb-8">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-2">
                        Roster
                    </div>
                    <h1 className="font-display text-bone text-4xl tracking-tightest leading-[1]">
                        Clients
                    </h1>
                </div>
                <button
                    onClick={() => setShowNew(true)}
                    data-testid="new-client-button"
                    className="btn-ember inline-flex items-center gap-2 h-11 px-5 text-xs uppercase tracking-[0.2em]"
                >
                    <Plus className="w-4 h-4" />
                    New client
                </button>
            </div>

            {busy ? (
                <div className="flex items-center gap-2 text-bone-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading…
                </div>
            ) : clients.length === 0 ? (
                <div className="border border-white/10 bg-ink-800/40 p-12 text-center text-bone-300">
                    No clients yet. Convert a brief from the inbox or add one
                    manually.
                </div>
            ) : (
                <ul className="border-t border-white/10">
                    {clients.map((c) => (
                        <li
                            key={c.id}
                            className="border-b border-white/10"
                        >
                            <Link
                                to={`/admin/clients/${c.id}`}
                                data-testid={`client-row-${c.id}`}
                                className="grid grid-cols-12 gap-4 items-center py-5 px-2 hover:bg-white/5 transition-colors"
                            >
                                <div className="col-span-5">
                                    <div className="font-display text-bone text-lg tracking-tight">
                                        {c.name}
                                    </div>
                                    <div className="text-bone-500 text-xs">
                                        {c.email}
                                    </div>
                                </div>
                                <div className="col-span-3 text-bone-300 text-sm truncate">
                                    {c.company || "—"}
                                </div>
                                <div className="col-span-2 text-xs uppercase tracking-[0.2em] text-bone-500">
                                    {new Date(c.created_at).toLocaleDateString()}
                                </div>
                                <div className="col-span-2 justify-self-end">
                                    <span
                                        className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-1 ${
                                            STATUS_BADGE[c.status] ||
                                            "border-white/15 text-bone-300"
                                        }`}
                                    >
                                        {c.status}
                                    </span>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}

            {showNew && (
                <div className="fixed inset-0 z-40 bg-ink/80 backdrop-blur-sm flex items-center justify-center p-6">
                    <form
                        onSubmit={onCreate}
                        data-testid="new-client-form"
                        className="bg-ink-800 border border-white/15 w-full max-w-md p-8 relative"
                    >
                        <button
                            type="button"
                            onClick={() => setShowNew(false)}
                            className="absolute top-4 right-4 text-bone-500 hover:text-bone"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <h2 className="font-display text-bone text-2xl tracking-tight mb-2">
                            New client
                        </h2>
                        <p className="text-bone-500 text-sm mb-8">
                            We'll email them a magic link to set their
                            password.
                        </p>
                        <div className="space-y-5">
                            <Input
                                label="Name"
                                testid="client-form-name"
                                value={form.name}
                                onChange={(v) =>
                                    setForm((f) => ({ ...f, name: v }))
                                }
                            />
                            <Input
                                label="Email"
                                type="email"
                                testid="client-form-email"
                                value={form.email}
                                onChange={(v) =>
                                    setForm((f) => ({ ...f, email: v }))
                                }
                            />
                            <Input
                                label="Company (optional)"
                                testid="client-form-company"
                                value={form.company}
                                onChange={(v) =>
                                    setForm((f) => ({ ...f, company: v }))
                                }
                            />
                            <div className="pt-3 border-t border-white/10">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                    Plan (optional)
                                </div>
                                <Input
                                    label="Plan name"
                                    testid="client-form-plan"
                                    value={form.plan}
                                    onChange={(v) =>
                                        setForm((f) => ({ ...f, plan: v }))
                                    }
                                />
                                <Input
                                    label="Plan summary"
                                    testid="client-form-plan-summary"
                                    multiline
                                    value={form.plan_summary}
                                    onChange={(v) =>
                                        setForm((f) => ({
                                            ...f,
                                            plan_summary: v,
                                        }))
                                    }
                                />
                            </div>
                            <div className="pt-3 border-t border-white/10">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                    Account manager (optional)
                                </div>
                                <Input
                                    label="Manager name"
                                    testid="client-form-manager-name"
                                    value={form.manager_name}
                                    onChange={(v) =>
                                        setForm((f) => ({
                                            ...f,
                                            manager_name: v,
                                        }))
                                    }
                                />
                                <Input
                                    label="Manager role"
                                    testid="client-form-manager-role"
                                    value={form.manager_role}
                                    onChange={(v) =>
                                        setForm((f) => ({
                                            ...f,
                                            manager_role: v,
                                        }))
                                    }
                                />
                                <Input
                                    label="Manager email"
                                    type="email"
                                    testid="client-form-manager-email"
                                    value={form.manager_email}
                                    onChange={(v) =>
                                        setForm((f) => ({
                                            ...f,
                                            manager_email: v,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            data-testid="client-form-submit"
                            className="mt-8 btn-ember w-full inline-flex items-center justify-center gap-2 h-12 text-xs uppercase tracking-[0.2em]"
                        >
                            <Mail className="w-4 h-4" />
                            Create & send invite
                        </button>
                    </form>
                </div>
            )}
        </AdminShell>
    );
}

function Input({ label, type = "text", value, onChange, testid, multiline }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    data-testid={testid}
                    className="w-full bg-transparent border border-white/15 focus:border-ember outline-none p-3 text-bone text-sm resize-none"
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-2 text-bone font-display text-base"
                />
            )}
        </label>
    );
}
